import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SessionTimeout.css';

const SessionTimeout = ({ onLogout, timeoutMinutes = 30 }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const navigate = useNavigate();

  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Activity events to monitor
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

  // Convert minutes to milliseconds
  const timeoutDuration = timeoutMinutes * 60 * 1000;
  const warningDuration = 60 * 1000; // Show warning 1 minute before timeout

  const logout = useCallback(() => {
    // Clear all timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    // Remove event listeners
    events.forEach(event => {
      window.removeEventListener(event, resetTimeout);
    });

    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Call parent logout handler
    if (onLogout) onLogout();

    // Navigate to login
    navigate('/login');
  }, [navigate, onLogout]);

  const startCountdown = useCallback(() => {
    setCountdown(60);

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [logout]);

  const showTimeoutWarning = useCallback(() => {
    setShowWarning(true);
    startCountdown();
  }, [startCountdown]);

  const resetTimeout = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    // Hide warning if shown
    if (showWarning) {
      setShowWarning(false);
    }

    // Set warning timer (show warning 1 minute before logout)
    warningTimeoutRef.current = setTimeout(() => {
      showTimeoutWarning();
    }, timeoutDuration - warningDuration);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      logout();
    }, timeoutDuration);
  }, [logout, showTimeoutWarning, timeoutDuration, warningDuration, showWarning]);

  const continueSession = () => {
    setShowWarning(false);
    resetTimeout();
  };

  useEffect(() => {
    // Add event listeners for user activity
    events.forEach(event => {
      window.addEventListener(event, resetTimeout);
    });

    // Start initial timeout
    resetTimeout();

    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

      events.forEach(event => {
        window.removeEventListener(event, resetTimeout);
      });
    };
  }, [resetTimeout]);

  if (!showWarning) return null;

  return (
    <div className="session-timeout-overlay">
      <div className="session-timeout-modal">
        <div className="session-timeout-header">
          <div className="warning-icon">⚠️</div>
          <h2>Session Timeout Warning</h2>
        </div>

        <div className="session-timeout-body">
          <p>Your session is about to expire due to inactivity.</p>
          <p className="countdown-text">
            You will be logged out in <strong>{countdown} second{countdown !== 1 ? 's' : ''}</strong>.
          </p>
          <p className="help-text">Click "Continue Session" to stay logged in.</p>
        </div>

        <div className="session-timeout-actions">
          <button className="btn btn-primary" onClick={continueSession}>
            Continue Session
          </button>
          <button className="btn btn-secondary" onClick={logout}>
            Logout Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeout;
